<template>
  <v-form v-if="user"
          ref="form"
          :disabled="isDisabled"
          @submit.prevent="submit">
    <v-card class="mb-4">
      <v-card-title>
        {{ name }} - {{ user.email }}
      </v-card-title>
      <v-card-text v-if="isSuperUser">
        You cannot change this user's Metabase profile because they are a Metabase super user.
      </v-card-text>
      <v-card-text v-else-if="mlUser && mlUser.is_inactive">
        You cannot change this user's Metabase profile because their Mobile Locker account is inactive.
      </v-card-text>
      <template v-else>
        <v-card-text class="pb-0">
          <div class="d-flex">
            <div class="mr-2 font-weight-bold">
              Last Metabase login:
            </div>
            <div>
              <template v-if="user.last_login">
                {{ user.last_login | datetime }}
              </template>
              <template v-else>
                Never
              </template>
            </div>
          </div>

          <template v-if="hasAccount">
            <v-switch v-model="form.is_active"
                      dense
                      :label="form.is_active ? 'Can use Metabase' : 'Cannot use Metabase'"
                      :error-messages="form.errors.get('is_active')">
            </v-switch>

            <template v-if="form.is_active">
              <v-select v-model="form.group_ids"
                        :items="groups"
                        item-value="id"
                        item-text="name"
                        label="Metabase groups"
                        outlined
                        multiple
                        chips
                        small-chips
                        deletable-chips
                        clearable
                        required
                        :rules="[rules.required]"
                        :error-messages="form.errors.get('group_ids')">
              </v-select>
            </template>
          </template>
          <template v-else>
            {{ name }} doesn't have a Mobile Locker account so you cannot manage their permissions here. Contact Mobile Locker support.
          </template>
        </v-card-text>
        <v-card-actions v-if="hasAccount"
                        class="px-4">
          <v-btn type="submit"
                 color="primary"
                 :disabled="isDisabled"
                 small>
            Update Profile
          </v-btn>
        </v-card-actions>
      </template>
    </v-card>
  </v-form>
</template>
<script>
import {Form} from 'vform';
import {isObject} from 'lodash';
import metabaseService from '../../../services/metabaseService';
import {mapGetters} from 'vuex';
import {formValidation} from '../../../plugins';
import DOMPurify from 'dompurify';

export default {
  mixins: [formValidation],
  props: {
    groups: {
      type: Array,
      required: true,
    },
    user: {
      type: Object,
      required: true,
    },
    seatsAvailable: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      form: new Form({
        is_active: this.user.is_active,
        group_ids: this.user.group_ids,
      })
    }
  },
  computed: {
    ...mapGetters([
      'currentUser',
      'team',
    ]),
    isActive() {
      return this.user.is_active;
    },
    isSuperUser() {
      return this.user.is_superuser;
    },
    hasAccount() {
      return isObject(this.mlUser);
    },
    isDisabled() {
      if (this.isActive === false && this.seatsAvailable === 0) {
        return true;
      }
      if (this.mlUser && this.mlUser.is_inactive) {
        return true;
      }
      return this.isSuperUser || this.form.busy;
    },
    userID() {
      if (this.mlUser) {
        return this.mlUser.id;
      }
      return null;
    },
    mlUser() {
      return this.user.mobilelocker_user;
    },
    name() {
      return this.mlUser
        ? this.mlUser.name
        : `${this.user.common_name}`;
    }
  },
  methods: {
    submit() {
      if (this.form.busy) {
        return;
      }
      if (this.hasAccount === false) {
        return;
      }
      if (this.$refs.form.validate() === false) {
        return;
      }
      metabaseService.updateUser(this.userID, this.form)
        .then(() => {
          this.$emit('updated');
          this.$toast.success(`Updated Metabase user.`);
        })
        .catch(err => {
          if (err.response && err.response.data) {
            this.$toast.error(DOMPurify.sanitize(err.response.data.message));
          }
        });
    }
  }
}
</script>
